// Collapsing repeats out of an order search.
//
// The service master returns the same test more than once — registered under
// several groups, or entered twice — and the medicine typeahead is a SNOMED
// synonym search, so one drug comes back once per synonym. Both lists were
// rendered exactly as received, and a prescriber picking from twelve rows that
// say "Paracetamol" is choosing at random between things that may not be the
// same.
//
// A ROW IS ONLY MERGED WITH ONE IT CANNOT BE TOLD APART FROM. Collapsing on the
// displayed name alone — which the investigations tab was already doing —
// threw away a row that shares a name and charges a different amount, and the
// biller never learned the other one existed. Collapsing on a code alone would
// do the same the other way round: the card's code falls back to a SNOMED
// concept, which two differently named services can share. So each key holds
// everything the list shows that could differ, and no row the search showed
// before this merging existed is hidden by it. A row that cannot be identified
// at all is kept: showing a duplicate is a smaller failure than hiding a service.

/** Case and outer spaces ignored — how the investigations list always compared names. */
const fold = (value) => String(value == null ? "" : value).trim().toLowerCase();

/**
 * Keep the first row of each identity, in the order they arrived.
 *
 * `identity` returning null means "cannot be keyed" — that row is always kept.
 *
 * @param {unknown[]|null|undefined} rows
 * @param {(row: unknown) => string|null} identity
 * @returns {unknown[]}
 */
export function dedupeBy(rows, identity) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const key = identity(row);
    if (key == null) {
      out.push(row);
      return;
    }
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

/**
 * A billable service: its name, its code AND its rate. Two services sharing a
 * name but not a code or a price both stay listed; two with different names
 * never merge, even under one code.
 *
 * Reads both field spellings because the two search shapes disagree — the
 * charge-capture mapper emits `code`/`rate`, the raw master rows carry
 * `service_code`/`fee`.
 */
export function serviceIdentity(row) {
  const name = fold(row && row.name);
  if (!name) return null;
  const code = fold(row && (row.service_code || row.code));
  const rawRate = row && (row.rate != null ? row.rate : row.fee);
  const rate = rawRate == null || rawRate === "" ? "" : Number(rawRate);
  return `name:${name}|code:${code}|rate:${rate}`;
}

/** A SNOMED medicine term as the suggestion list shows it: the name and, under
 *  it, the generic. Concept ids differ per synonym and are never shown. */
export function medicineTermIdentity(row) {
  const name = fold(row && row.name);
  return name ? `term:${name}|generic:${fold(row && row.generic)}` : null;
}

/** A pharmacy catalogue row: the inventory item and the stock shown for it —
 *  two rows for one item that report different stock both stay. */
export function pharmacyItemIdentity(row) {
  const id = String((row && (row.item_id || row.id)) || "").trim();
  if (!id) return null;
  return `item:${id}|stock:${row.stock == null ? "" : row.stock}|in:${Boolean(row.has_stock)}`;
}
